import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-4 border-red-300 shadow-xl">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              onClick={() => navigate(-1)}
              variant="outline"
              className="border-2 border-red-400"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
          <h1 className="text-4xl font-bold mb-2" style={{
            background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>Privacy Policy</h1>
          <p className="text-gray-700 text-lg font-medium">Last updated: November 2, 2025</p>
        </div>

        <Card className="border-4 border-red-400 shadow-2xl bg-white/90 backdrop-blur-sm">
          <CardContent className="p-8 space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                Netix Fibernet ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Referral Program platform. Please read this privacy policy carefully.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">2.1 Personal Information</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                We collect personal information that you voluntarily provide when registering for our referral program:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Full name (first and last name)</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Payment information (bank account details or UPI)</li>
                <li>Referral code and usage data</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">2.2 Customer Information</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                When you refer a customer, we collect:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Customer name and contact details</li>
                <li>Service address and location coordinates</li>
                <li>Product/service preferences</li>
                <li>Order and transaction information</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">2.3 Automatically Collected Information</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>IP address and browser information</li>
                <li>Device type and operating system</li>
                <li>Pages visited and time spent on platform</li>
                <li>Referral link clicks and conversions</li>
                <li>Session data and cookies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">3. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                We use the collected information for the following purposes:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>To create and manage your referral account</li>
                <li>To track referrals and calculate commissions</li>
                <li>To process commission payments</li>
                <li>To communicate with you about your account and referrals</li>
                <li>To provide customer support</li>
                <li>To prevent fraud and ensure program integrity</li>
                <li>To improve our services and user experience</li>
                <li>To comply with legal obligations</li>
                <li>To send promotional materials (with your consent)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">4. Information Sharing and Disclosure</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">We may share your information with:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Service Providers:</strong> Third-party vendors who assist in payment processing, analytics, and hosting</li>
                <li><strong>Business Partners:</strong> Only as necessary to fulfill referral orders</li>
                <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets</li>
              </ul>
              
              <p className="text-gray-700 leading-relaxed mt-3">
                <strong>We do NOT:</strong> Sell your personal information to third parties for marketing purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Encrypted data transmission (SSL/TLS)</li>
                <li>Secure password hashing and authentication</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and employee training</li>
                <li>Secure cloud infrastructure</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security of your data.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                We use cookies and similar tracking technologies to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Track referral link performance</li>
                <li>Maintain your login session</li>
                <li>Analyze website traffic and user behavior</li>
                <li>Personalize your experience</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                You can control cookie preferences through your browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Your Privacy Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Data Portability:</strong> Receive your data in a structured format</li>
                <li><strong>Withdraw Consent:</strong> Revoke consent for data processing</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                To exercise these rights, contact us at support@netixfibernet.com
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your personal information for as long as your account is active or as needed to provide services. After account closure, we may retain certain information for legal compliance, dispute resolution, and fraud prevention purposes (typically 7 years).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">9. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our referral program is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we discover that we have collected information from a child, we will promptly delete it.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">10. International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your data in accordance with this privacy policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">11. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this privacy policy periodically. We will notify you of significant changes via email or a prominent notice on our platform. The "Last Updated" date at the top indicates when the policy was last revised.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">12. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                If you have questions or concerns about this privacy policy, please contact us:
              </p>
              <div className="mt-3 text-gray-700 space-y-1">
                <p><strong>Email:</strong> privacy@netixfibernet.com</p>
                <p><strong>Support:</strong> support@netixfibernet.com</p>
                <p data-testid="privacy-contact-mobile"><strong>Phone:</strong> +91 93199 19988</p>
                <p data-testid="privacy-office-phone"><strong>Office Phone:</strong> 06276 - 459015</p>
                <p><strong>Office Locations:</strong> Delhi, Haryana, Bihar, Uttar Pradesh</p>
              </div>
            </section>

            <div className="mt-8 pt-6 border-t border-gray-300">
              <p className="text-sm text-gray-600 italic">
                By using the Netix Fibernet Referral Program, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
